<template>
    <div class="csv-importer">
        <!-- Step 1: File Selection -->
        <div class="file-upload-section">
            <v-file-input
                v-model="selectedFile"
                label="Choose CSV file"
                accept=".csv"
                prepend-icon="mdi-file-delimited"
                show-size
                @change="onFileSelected"
                :disabled="loading"
                clearable
            ></v-file-input>
        </div>

        <!-- Step 2: Column Mapping (shown after file is parsed) -->
        <div v-if="csvData.length > 0 && !mappingComplete" class="mapping-section">
            <v-card class="mb-4">
                <v-card-title>Map CSV Columns to Fields</v-card-title>
                <v-card-text>
                    <p class="text-caption mb-4">
                        Map the columns from your CSV file to the corresponding fields. Auto-mapping has been applied
                        based on column names.
                    </p>

                    <v-row>
                        <v-col v-for="(field, fieldKey) in fieldDefinitions" :key="fieldKey" cols="12" sm="6" md="4">
                            <v-select
                                v-model="fieldMapping[fieldKey]"
                                :label="field.label + (field.required ? ' *' : '')"
                                :items="csvColumnOptions"
                                item-title="text"
                                item-value="value"
                                clearable
                                density="compact"
                            >
                                <template v-slot:prepend-item>
                                    <v-list-item title="(Do not import)" :value="null"></v-list-item>
                                    <v-divider></v-divider>
                                </template>
                            </v-select>
                        </v-col>
                    </v-row>

                    <div class="mt-4">
                        <v-btn color="primary" @click="applyMapping" :disabled="!canApplyMapping">
                            <v-icon left>mdi-check</v-icon>
                            Apply Mapping
                        </v-btn>
                        <v-btn class="ml-2" @click="resetMapping">
                            <v-icon left>mdi-refresh</v-icon>
                            Reset
                        </v-btn>
                    </div>
                </v-card-text>
            </v-card>
        </div>

        <!-- Step 3: Preview (shown after mapping is applied) -->
        <div v-if="mappingComplete && mappedData.length > 0" class="preview-section">
            <v-expansion-panels>
                <v-expansion-panel>
                    <v-expansion-panel-title>
                        <div class="d-flex justify-space-between align-center w-100">
                            <span>
                                <v-icon class="mr-2">mdi-table-eye</v-icon>
                                Data Preview ({{ mappedData.length }} rows)
                            </span>
                            <v-btn size="small" variant="text" @click.stop="editMapping" class="mr-2">
                                <v-icon left>mdi-pencil</v-icon>
                                Edit Mapping
                            </v-btn>
                        </div>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <div class="table-container">
                            <v-table density="compact" fixed-header height="400px">
                                <thead>
                                    <tr>
                                        <th class="text-left" v-for="header in previewHeaders" :key="header.value">
                                            {{ header.text }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(row, idx) in mappedData" :key="`row-${idx}`">
                                        <td v-for="header in previewHeaders" :key="header.value">
                                            {{ row[header.value] || '' }}
                                        </td>
                                    </tr>
                                </tbody>
                            </v-table>
                        </div>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>
        </div>

        <!-- Error Display -->
        <v-alert v-if="parseError" type="error" class="mt-4" closable @click:close="parseError = ''">
            {{ parseError }}
        </v-alert>
    </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, computed, watch } from 'vue';
import Papa from 'papaparse';
import * as _ from 'lodash';

interface FieldDefinition {
    label: string;
    required?: boolean;
}

interface FieldDefinitions {
    [key: string]: FieldDefinition;
}

interface Header {
    text: string;
    value: string;
}

interface CsvColumnOption {
    text: string;
    value: string;
}

export default defineComponent({
    name: 'CsvImporter',

    props: {
        fieldDefinitions: {
            type: Object as PropType<FieldDefinitions>,
            required: true
        },
        previewHeaders: {
            type: Array as PropType<Header[]>,
            required: true
        }
    },

    emits: ['data-loaded', 'data-cleared'],

    setup(props, { emit }) {
        const selectedFile = ref<File | null>(null);
        const csvData = ref<any[]>([]);
        const csvHeaders = ref<string[]>([]);
        const fieldMapping = ref<Record<string, string | null>>({});
        const mappingComplete = ref(false);
        const mappedData = ref<any[]>([]);
        const loading = ref(false);
        const parseError = ref('');

        // Generate column options for dropdowns
        const csvColumnOptions = computed<CsvColumnOption[]>(() => {
            return csvHeaders.value.map((header) => ({
                text: header,
                value: header
            }));
        });

        // Check if mapping can be applied (at least one field mapped)
        const canApplyMapping = computed(() => {
            return Object.values(fieldMapping.value).some((v) => v !== null && v !== undefined);
        });

        // Auto-map columns based on field labels and names
        // Only use exact and case-insensitive matching to avoid false positives
        const autoMapColumns = () => {
            const mapping: Record<string, string | null> = {};
            const csvHeadersLower = csvHeaders.value.map((h) => h.toLowerCase().trim());

            Object.keys(props.fieldDefinitions).forEach((fieldKey) => {
                const fieldDef = props.fieldDefinitions[fieldKey];
                const fieldLabel = fieldDef.label;
                let matchIndex = -1;

                // Strategy 1: Try exact match with field key (highest priority)
                matchIndex = csvHeaders.value.findIndex((h) => h === fieldKey);

                // Strategy 2: Try case-insensitive match with field key
                if (matchIndex === -1) {
                    matchIndex = csvHeadersLower.findIndex((h) => h === fieldKey.toLowerCase());
                }

                // Strategy 3: Try exact match with field label
                if (matchIndex === -1 && fieldLabel) {
                    matchIndex = csvHeaders.value.findIndex((h) => h === fieldLabel);
                }

                // Strategy 4: Try case-insensitive match with field label
                if (matchIndex === -1 && fieldLabel) {
                    matchIndex = csvHeadersLower.findIndex((h) => h === fieldLabel.toLowerCase().trim());
                }

                // Removed aggressive partial matching strategies to prevent false positives
                // Users can manually map if column names don't match exactly

                mapping[fieldKey] = matchIndex !== -1 ? csvHeaders.value[matchIndex] : null;
            });

            fieldMapping.value = mapping;
        };

        // Parse CSV file
        const onFileSelected = (event: Event) => {
            const file = selectedFile.value;
            if (!file) {
                resetState();
                emit('data-cleared');
                return;
            }

            loading.value = true;
            parseError.value = '';

            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                delimiter: ',',
                complete: (results) => {
                    if (results.errors.length > 0) {
                        parseError.value = `CSV parsing errors: ${results.errors.map((e) => e.message).join(', ')}`;
                        loading.value = false;
                        return;
                    }

                    if (results.data.length === 0) {
                        parseError.value = 'CSV file is empty or has no valid data rows.';
                        loading.value = false;
                        return;
                    }

                    csvData.value = results.data;
                    csvHeaders.value = results.meta.fields || [];

                    // Auto-map columns
                    autoMapColumns();

                    loading.value = false;
                },
                error: (error) => {
                    parseError.value = `Error reading file: ${error.message}`;
                    loading.value = false;
                }
            });
        };

        // Apply the mapping and create mapped data
        const applyMapping = () => {
            const mapped = csvData.value.map((row) => {
                const mappedRow: Record<string, any> = {};

                Object.keys(fieldMapping.value).forEach((fieldKey) => {
                    const csvColumn = fieldMapping.value[fieldKey];
                    if (csvColumn) {
                        mappedRow[fieldKey] = _.trim(row[csvColumn]);
                    }
                });

                return mappedRow;
            });

            mappedData.value = mapped;
            mappingComplete.value = true;

            // Emit the mapped data to parent
            emit('data-loaded', mapped);
        };

        // Reset mapping
        const resetMapping = () => {
            autoMapColumns();
        };

        // Edit mapping (go back to mapping step)
        const editMapping = () => {
            mappingComplete.value = false;
        };

        // Reset entire state
        const resetState = () => {
            csvData.value = [];
            csvHeaders.value = [];
            fieldMapping.value = {};
            mappingComplete.value = false;
            mappedData.value = [];
            parseError.value = '';
        };

        // Watch for file being cleared
        watch(selectedFile, (newFile) => {
            if (!newFile) {
                resetState();
                emit('data-cleared');
            }
        });

        return {
            selectedFile,
            csvData,
            csvHeaders,
            csvColumnOptions,
            fieldMapping,
            mappingComplete,
            mappedData,
            loading,
            parseError,
            canApplyMapping,
            onFileSelected,
            applyMapping,
            resetMapping,
            editMapping
        };
    }
});
</script>

<style scoped>
.csv-importer {
    width: 100%;
}

.file-upload-section {
    margin-bottom: 1rem;
}

.mapping-section,
.preview-section {
    margin-top: 1rem;
}

.table-container {
    overflow-x: auto;
}

select {
    border: 1px solid !important;
}
</style>
